/**
 * A title turned into an address: lowercase, unaccented, hyphen-joined.
 *
 * Shared by the section dialogs, which held two byte-identical copies. The
 * space, event and product forms hold their own variants that differ in what
 * they keep — underscores in one, trimming order in another — so they are left
 * where they are rather than quietly changed by a refactor.
 */
export const generateSlug = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/**
 * What a slug field keeps while someone types in it: the same alphabet as
 * `generateSlug`, but a trailing hyphen survives, or "my-" could never become
 * "my-space". Leading hyphens and underscores go: the API refuses a slug that
 * starts with `_`, and `_` is not in the alphabet anyway.
 */
export const typedSlug = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')

/**
 * Words a section slug may not be: the app's fixed first-segment routes and
 * the names it keeps for itself. Mirrors `memberfy-api/src/config/reservedSlugs.js`
 * (a850699), which is what decides; this only warns before sending. Spaces are
 * the second segment, where no fixed route lives, so they only refuse a
 * leading `_`.
 */
export const RESERVED_SECTION_SLUGS: ReadonlySet<string> = new Set([
  'admin', 'pricing', 'billing', 'checkout', 'checkout-success', 'products', 'settings', 'account',
  'members', 'monetization', 'set-password', 'support-entry', 'login', 'logout', 'signup', 'register', 'auth',
  'verify', 'payment', 'pagamento', 'offer', 'oferta', 'oauth',
  'content', 'events', 'courses', 'ranking', 'gallery',
  'api', 'docs', 'sdk', 'mcp', 'well-known',
  'assets', 'static', 'public', 'health', 'favicon', 'robots', 'sitemap', 'manifest'
])

/** Why the API would refuse this slug, or `null`. */
export const slugProblem = (slug: string, kind: 'section' | 'space'): 'reserved' | null => {
  const value = slug.trim().toLowerCase()
  if (value.startsWith('_')) return 'reserved'
  if (kind === 'section' && RESERVED_SECTION_SLUGS.has(value)) return 'reserved'
  return null
}

/** The API's message for the slug field, when that is what it refused. */
export const slugErrorFrom = (err: any): string | null => {
  const errors = err?.response?.data?.errors
  const onSlug = Array.isArray(errors) ? errors.find((e: any) => e?.param === 'slug') : null
  return onSlug?.message || null
}
